import React, { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeStyleSets } from '@fluentui/react';
import { IPIconButton, IPLabel, IPLink, IPSeparator, IPToggle } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackStylesHeight100PaddingMedium, globalStackTokensGapSmall } from 'globalStyles';
import AppContext from 'App/AppContext';
import { navigateToExternalUrl } from 'utils/url';

interface IUserDialogProps {
  isOpen: boolean;
  logout?: () => void;
  toggleDialog: () => void;
}

const UserAccountDialog: React.FunctionComponent<IUserDialogProps> = (props: IUserDialogProps) => {
  const { t } = useTranslation(['translation']);
  const appContext = useContext(AppContext);

  const styles = mergeStyleSets({
    callout: {
      maxWidth: 300,
    },
    button: {
      border: 0,
    },
  });

  return (
    <IPCallout
      className={styles.callout}
      alignTargetEdge={true}
      gapSpace={0}
      target=".calloutTargetAuthButton"
      isBeakVisible={false}
      directionalHint={IPDirectionalHint.bottomRightEdge}
      hidden={!props.isOpen}
      onDismiss={props.toggleDialog}
    >
      <IPStack styles={globalStackStylesHeight100PaddingMedium} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPLabel>{appContext.user.tenant.name}</IPLabel>
        </IPStack.Item>
        <IPStack.Item>
          <IPPersona text={appContext.user.name} secondaryText={appContext.user.email} size={IPPersonaSize.size48} />
        </IPStack.Item>
        <IPStack.Item>
          <IPStack>
            <IPLink onClick={() => navigateToExternalUrl(appContext.user.getMyAccountURL(), '', '')}>
              {t('translation:NavBar.UserDialog.ViewAccount')}
            </IPLink>
            <IPLink onClick={() => (props.logout ? props.logout() : undefined)}>
              {t('translation:NavBar.UserDialog.Button-SignOut')}
            </IPLink>
          </IPStack>
        </IPStack.Item>
        <IPSeparator />
        <IPStack.Item>
          <IPLabel>{t('translation:Settings.Theme-Switch')}</IPLabel>
          <IPToggle
            checked={appContext.useDarkMode}
            onText={t('translation:General.Toggle.On')}
            offText={t('translation:General.Toggle.Off')}
            onChange={() => {
              appContext.setUseDarkMode(!appContext.useDarkMode);
            }}
          />
        </IPStack.Item>
      </IPStack>
    </IPCallout>
  );
};

interface IUserAccountProps {
  logout?: () => void;
}

const UserAccount: React.FunctionComponent<IUserAccountProps> = (props: IUserAccountProps) => {
  const appContext = useContext(AppContext);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const toggleDialog = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Fragment>
      <IPIconButton className="calloutTargetAuthButton" onClick={toggleDialog}>
        <IPPersona text={appContext.user.name} size={IPPersonaSize.size32} hidePersonaDetails={true} />
      </IPIconButton>
      <UserAccountDialog logout={props.logout} isOpen={isOpen} toggleDialog={toggleDialog} />
    </Fragment>
  );
};

export default UserAccount;
